import { Box, Flex, Text } from '@chakra-ui/react';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonIcon, Logo, Modal, useToast } from '@morressier/alembic';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useUpdateJournalSettings } from 'src/api/mutations';
import LogoUrlFileUpload from './LogoUrlFileUpload';

export const LogoImage = ({ logoUrl }: { logoUrl: string }) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  return (
    <>
      <Box onClick={() => setIsPreviewOpen(true)} cursor="pointer">
        <Logo
          src={logoUrl}
          maxWidth="200px"
          maxH="40px"
          minH="40px"
          alt="Client Logo"
        />
      </Box>
      <Modal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        width={{ base: 'calc(100vw - 16px)', md: '32rem' } as any}
      >
        <Flex direction="column" align="center" gap={4} padding={8}>
          <Text textStyle="headingGroup">Preview</Text>
          <img
            src={logoUrl}
            alt="Logo preview"
            style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
          />
          <Flex justifyContent="flex-end" width="100%">
            <Button variant="secondary" onClick={() => setIsPreviewOpen(false)}>
              Close
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};

const EditLogoModal = ({
  journalId,
  organizationId
}:
{
  journalId: string;
  organizationId: string;
}) => {
  const toast = useToast();

  const { watch, getValues, handleSubmit, setValue, reset } = useFormContext();
  const logoUrl = watch('config.logoUrl');
  const logoFile = watch('logoFile');

  const [isOpen, setIsOpen] = useState(false);

  const toggleModal = () => setIsOpen(prev => !prev);
  const onClose = () => {
    reset();
    setIsOpen(false);
  };

  const updateSettingMutation = useUpdateJournalSettings(journalId);

  const onSubmit = async () => {
    const { logoFile } = getValues();

    const payload = {
      logoUrl: logoFile?.url
    };

    const res = await updateSettingMutation.mutateAsync({
      data: {},
      config: payload
    });

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: 'Journal Config Edited',
        isClosable: true
      });
      setValue('config.logoUrl', logoFile?.url);
      return setIsOpen(false);
    }
    return toast({
      variant: 'error',
      title:
        typeof res?.message === 'string'
          ? res?.message
          : 'Something went wrong',
      isClosable: true
    });
  };

  const isLoading = updateSettingMutation?.isLoading;
  return (
    <>
      <Flex direction={'column'}>
        <Flex alignItems={'center'}>
          <Text textStyle="headingItem">Logo</Text>
          <ButtonIcon
            onClick={toggleModal}
            ariaLabel="edit-config"
            icon={<FontAwesomeIcon icon={faEdit} />}
          />
        </Flex>
        {logoUrl ? (
          <LogoImage logoUrl={logoUrl} />
        ) : (
          <Text textStyle={'textBody'}>No value provided</Text>
        )}
      </Flex>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        width={{ base: 'calc(100vw - 16px)', md: '42rem' } as any}
        closeButtonProps={{
          hidden: true
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex direction={'column'} gap={4} padding={8}>
            <Flex fontSize={32}>
              <FontAwesomeIcon icon={faEdit} />
            </Flex>
            <Text textStyle="headingGroup">Edit Logo</Text>
            <Flex flexDirection="column" gap={4}>
              <LogoUrlFileUpload
                label="Logo"
                field={'config.logoUrl'}
                tempField="logoFile"
                organizationId={organizationId}
                isRequired={true}
              />
            </Flex>
            <Flex
              justifyContent="flex-end"
              gap={2}
              alignItems="center"
              marginTop={8}
            >
              <Button
                variant="secondary"
                onClick={onClose}
                isDisabled={isLoading}
              >
                Exit Without Saving
              </Button>
              <Button variant="primary" type="submit" isLoading={isLoading} disabled={logoFile?.isUploading}>
                Save Changes
              </Button>
            </Flex>
          </Flex>
        </form>
      </Modal>
    </>
  );
};

export default EditLogoModal;
